import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { printDocumentTicket80mm } from '../utils/ticket80mmPrint';
import {
  Search, FileText, Receipt, ShoppingCart, Ticket, Filter,
  Link2, ExternalLink, ChevronDown, ChevronUp, Download, X,
  Printer, Mail, Eye
} from 'lucide-react';

const TYPE_CONFIG = {
  'DEVIS':           { label: 'Devis',           color: 'bg-blue-500/20 text-blue-400',    border: 'border-blue-500/30' },
  'FACTURE':         { label: 'Facture',         color: 'bg-emerald-500/20 text-emerald-400', border: 'border-emerald-500/30' },
  'BON DE COMMANDE': { label: 'Bon de commande', color: 'bg-orange-500/20 text-orange-400', border: 'border-orange-500/30' },
  'TICKET':          { label: 'Ticket',          color: 'bg-purple-500/20 text-purple-400', border: 'border-purple-500/30' },
};

// Display label i18n keys per backend document type. Keys stay backend values;
// only the human label is localized (fallback to TYPE_CONFIG.label / raw type).
const TYPE_I18N_KEY = {
  'DEVIS':           'allDocs.type.devis',
  'FACTURE':         'allDocs.type.facture',
  'BON DE COMMANDE': 'allDocs.type.bonCommande',
  'TICKET':          'allDocs.type.ticket',
};

// Backend FR status values → i18n keys for display only (fallback to raw value).
const STATUS_I18N_KEY = {
  'devis': 'allDocs.status.devis', 'en_attente': 'allDocs.status.enAttente',
  'accepte': 'allDocs.status.accepte', 'refuse': 'allDocs.status.refuse',
  'payee': 'allDocs.status.payee', 'commande': 'allDocs.status.commande',
  'En Cours': 'allDocs.status.enCours', 'Recu': 'allDocs.status.recu',
  'Livre': 'allDocs.status.livre', 'Termine': 'allDocs.status.termine',
};

const AllDocumentsPage = () => {
  const { api } = useAuth();
  const { t, formatCurrency, formatDate } = useLanguage();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // Localized display helpers (raw backend value preserved as fallback).
  const typeLabel = (type) => (TYPE_I18N_KEY[type] ? t(TYPE_I18N_KEY[type]) : (TYPE_CONFIG[type]?.label || type));
  const statusLabel = (status) => (STATUS_I18N_KEY[status] ? t(STATUS_I18N_KEY[status]) : (status || '-'));

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/documents/all');
      setDocs(res.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally { setLoading(false); }
  };

  // Phase C-fix (2026-05) — mapping (doc.source, doc.type) → backend endpoint.
  // Backend /api/documents/all emits source ∈ {calcul, deplacement, commande,
  // reparation, vente_historique}. The previous mapping compared against
  // {devis, facture, commande, vente_historique} which matched almost nothing
  // → "rien ne se passe au clic" on tickets/devis/factures.
  const resolveDocEndpoint = (doc) => {
    if (!doc) return null;
    if (doc.source === 'calcul')       return { url: `/calcul/documents/${doc.id}/ticket`, method: 'get' };
    if (doc.source === 'commande')     return { url: `/commandes/${doc.id}/ticket`, method: 'get' };
    if (doc.source === 'reparation')   return { url: `/devices/${encodeURIComponent(doc.ref)}/fiche-pdf`, method: 'get' };
    if (doc.source === 'vente_historique') return { url: `/factures/ticket/${encodeURIComponent(doc.ref)}`, method: 'get' };
    if (doc.source === 'deplacement') {
      if (doc.type === 'FACTURE')      return { url: `/facture-dep/${doc.id}/pdf`, method: 'get' };
      return { url: `/devis/${doc.id}/pdf`, method: 'get' }; // DEVIS deplacement
    }
    return null;
  };

  const resolveEmailEndpoint = (doc) => {
    if (!doc) return null;
    if (doc.source === 'commande') return `/commandes/${doc.id}/send-email`;
    if (doc.source === 'calcul')   return `/calcul/documents/${doc.id}/send-email`;
    return null; // others not yet wired — will surface a clean toast below.
  };

  const handlePrintDoc = async (doc) => {
    try {
      setActionLoading(`print-${doc.id}`);
      const queued = await printDocumentTicket80mm(api, doc);
      if (!queued) {
        alert(t('allDocs.printUnavailable', { source: doc.source }));
      }
    } catch (err) {
      alert(t('allDocs.error', { msg: err.response?.data?.detail || err.message }));
    } finally { setActionLoading(null); }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      setActionLoading(`dl-${doc.id}`);
      const endpoint = resolveDocEndpoint(doc);
      if (!endpoint) {
        alert(t('allDocs.downloadUnavailable', { source: doc.source }));
        return;
      }
      const response = endpoint.method === 'post'
        ? await api.post(endpoint.url, {}, { responseType: 'blob' })
        : await api.get(endpoint.url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = `${doc.type}_${doc.ref || doc.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 5000);
    } catch (err) {
      alert(t('allDocs.error', { msg: err.response?.data?.detail || err.message }));
    } finally { setActionLoading(null); }
  };

  const handleEmailDoc = async (doc) => {
    const emailEndpoint = resolveEmailEndpoint(doc);
    if (!emailEndpoint) {
      alert(t('allDocs.emailUnavailable', { source: doc.source }));
      return;
    }
    const email = prompt(t('allDocs.emailPrompt'), doc.client_email || '');
    if (!email) return;
    try {
      setActionLoading(`email-${doc.id}`);
      await api.post(emailEndpoint, { email });
      alert(t('allDocs.emailSent'));
    } catch (err) {
      alert(t('allDocs.error', { msg: err.response?.data?.detail || err.message }));
    } finally { setActionLoading(null); }
  };

  const filtered = useMemo(() => {
    let list = docs;
    if (typeFilter !== 'ALL') list = list.filter(d => d.type === typeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        (d.ref || '').toLowerCase().includes(q) ||
        (d.client_nom || '').toLowerCase().includes(q) ||
        (d.client_tel || '').includes(q)
      );
    }
    return list;
  }, [docs, typeFilter, search]);

  const stats = useMemo(() => {
    const s = { total: docs.length, DEVIS: 0, FACTURE: 0, 'BON DE COMMANDE': 0, TICKET: 0, totalAmount: 0 };
    docs.forEach(d => { s[d.type] = (s[d.type] || 0) + 1; s.totalAmount += d.total || 0; });
    return s;
  }, [docs]);

  const renderDate = (iso) => {
    if (!iso) return '-';
    try { return formatDate(iso, { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return String(iso).slice(0, 10); }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-white" data-testid="documents-page-title">{t('allDocs.title')}</h1>
            <p className="text-xs text-gray-500 mt-0.5">{t('allDocs.docsCount', { count: stats.total })}</p>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { id: 'devis', label: t('allDocs.stat.devis'), count: stats.DEVIS, icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10' },
            { id: 'factures', label: t('allDocs.stat.factures'), count: stats.FACTURE, icon: Receipt, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
            { id: 'bons-cmd', label: t('allDocs.stat.bonsCmd'), count: stats['BON DE COMMANDE'], icon: ShoppingCart, color: 'text-orange-400', bg: 'bg-orange-500/10' },
            { id: 'tickets', label: t('allDocs.stat.tickets'), count: stats.TICKET, icon: Ticket, color: 'text-purple-400', bg: 'bg-purple-500/10' },
            { id: 'total', label: t('allDocs.stat.total'), count: formatCurrency(stats.totalAmount, 'EUR', { maximumFractionDigits: 0 }), icon: null, color: 'text-white', bg: 'bg-white/[0.05]' },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} rounded-xl p-3 border border-white/[0.06]`} data-testid={`doc-stat-${s.id}`}>
              <div className="flex items-center gap-2">
                {s.icon && <s.icon className={`w-4 h-4 ${s.color}`} />}
                <span className="text-[11px] text-gray-500">{s.label}</span>
              </div>
              <div className={`text-lg font-bold ${s.color} mt-1`}>{s.count}</div>
            </div>
          ))}
        </div>

        {/* Search + Filters */}
        <div className="flex gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder={t('allDocs.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] text-white placeholder-gray-600 focus:border-white/20 outline-none"
              data-testid="documents-search"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 border transition-all ${
              typeFilter !== 'ALL' ? 'bg-white/10 border-white/20 text-white' : 'bg-white/[0.04] border-white/[0.08] text-gray-400 hover:text-white'
            }`}
            data-testid="documents-filter-btn"
          >
            <Filter className="w-4 h-4" />
            {t('allDocs.filter')}
            {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {/* Filter buttons */}
        {showFilters && (
          <div className="flex gap-2 flex-wrap" data-testid="documents-filter-bar">
            {['ALL', 'DEVIS', 'FACTURE', 'BON DE COMMANDE', 'TICKET'].map(ft => (
              <button
                key={ft}
                onClick={() => setTypeFilter(ft)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                  typeFilter === ft
                    ? 'text-white border-white/30'
                    : 'bg-white/[0.04] border-white/[0.06] text-gray-400 hover:text-white'
                }`}
                style={typeFilter === ft ? { background: 'var(--theme-primary)', borderColor: 'var(--theme-primary)' } : {}}
                data-testid={`filter-${ft.toLowerCase().replace(/ /g, '-')}`}
              >
                {ft === 'ALL' ? t('allDocs.filterAll') : typeLabel(ft)}
                {ft !== 'ALL' && <span className="ml-1 opacity-60">({ft === 'BON DE COMMANDE' ? stats['BON DE COMMANDE'] : stats[ft] || 0})</span>}
              </button>
            ))}
            {typeFilter !== 'ALL' && (
              <button onClick={() => setTypeFilter('ALL')} className="px-2 py-1.5 rounded-lg text-xs text-gray-500 hover:text-white">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
          <table className="w-full text-sm" data-testid="documents-table">
            <thead>
              <tr className="text-left" style={{ background: 'rgba(var(--theme-primary-rgb), 0.12)' }}>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: 'var(--theme-primary)' }}>{t('allDocs.col.reference')}</th>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: 'var(--theme-primary)' }}>{t('allDocs.col.type')}</th>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: 'var(--theme-primary)' }}>{t('allDocs.col.client')}</th>
                <th className="px-4 py-3 text-xs font-bold hidden md:table-cell" style={{ color: 'var(--theme-primary)' }}>{t('allDocs.col.telephone')}</th>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: 'var(--theme-primary)' }}>{t('allDocs.col.total')}</th>
                <th className="px-4 py-3 text-xs font-bold hidden md:table-cell" style={{ color: 'var(--theme-primary)' }}>{t('allDocs.col.statut')}</th>
                <th className="px-4 py-3 text-xs font-bold hidden lg:table-cell" style={{ color: 'var(--theme-primary)' }}>{t('allDocs.col.liaisons')}</th>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: 'var(--theme-primary)' }}>{t('allDocs.col.date')}</th>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: 'var(--theme-primary)' }}>{t('allDocs.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-500">{t('allDocs.loading')}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-500">{t('allDocs.empty')}</td></tr>
              ) : (
                filtered.map((doc, idx) => {
                  const cfg = TYPE_CONFIG[doc.type] || TYPE_CONFIG.DEVIS;
                  return (
                    <tr key={`${doc.source}-${doc.id}-${idx}`} className="border-t border-white/[0.04] hover:bg-white/[0.03] transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-cyan-400">{doc.ref || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${cfg.color} border ${cfg.border}`}>
                          {typeLabel(doc.type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white text-xs">{doc.client_nom || '-'}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell">{doc.client_tel || '-'}</td>
                      <td className="px-4 py-3 text-emerald-400 font-bold text-xs">{doc.total ? formatCurrency(doc.total) : '-'}</td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-[10px] px-2 py-0.5 rounded bg-white/[0.06] text-gray-300">
                          {statusLabel(doc.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {doc.linked_repair_ref && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 border border-orange-500/30">
                              <Link2 className="w-2.5 h-2.5" /> {doc.linked_repair_ref}
                            </span>
                          )}
                          {doc.linked_devis_ref && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">
                              <ExternalLink className="w-2.5 h-2.5" /> {doc.linked_devis_ref}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{renderDate(doc.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => handlePrintDoc(doc)} disabled={actionLoading === `print-${doc.id}`}
                            className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors disabled:opacity-50"
                            title={t('allDocs.action.print')} data-testid={`doc-print-${doc.id}`}>
                            {actionLoading === `print-${doc.id}` ? <div className="w-3 h-3 border border-emerald-400 border-t-transparent rounded-full animate-spin"></div> : <Eye className="w-3 h-3" />}
                          </button>
                          <button onClick={() => handleDownloadDoc(doc)} disabled={actionLoading === `dl-${doc.id}`}
                            className="p-1.5 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors disabled:opacity-50"
                            title={t('allDocs.action.download')} data-testid={`doc-download-${doc.id}`}>
                            {actionLoading === `dl-${doc.id}` ? <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin"></div> : <Download className="w-3 h-3" />}
                          </button>
                          <button onClick={() => handleEmailDoc(doc)} disabled={actionLoading === `email-${doc.id}`}
                            className="p-1.5 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors disabled:opacity-50"
                            title={t('allDocs.action.email')} data-testid={`doc-email-${doc.id}`}>
                            {actionLoading === `email-${doc.id}` ? <div className="w-3 h-3 border border-cyan-400 border-t-transparent rounded-full animate-spin"></div> : <Mail className="w-3 h-3" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer count */}
        {!loading && (
          <div className="text-xs text-gray-600 text-right">
            {filtered.length !== 1
              ? t('allDocs.footerCountPlural', { count: filtered.length })
              : t('allDocs.footerCount', { count: filtered.length })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AllDocumentsPage;
